import type { Engine } from '../core/Engine';
import type { GameModule } from '../core/GameModule';
import type { EventBus } from '../core/EventBus';
import type { WorldInfo } from '../config/WorldConfig';
import type { TravelConfig } from '../config/TravelConfig';
import type { FogState } from '../world/FogState';
import type { TravelLineRenderer } from '../objects/TravelLineRenderer';
import type { FleetRenderer } from '../objects/FleetRenderer';
import type { FleetState } from './FleetState';
import type { FleetIndicator } from '../objects/FleetIndicator';
import type { PathfindingResponse } from '../workers/pathfinding.types';
import { TravelState } from './TravelState';
import { TravelAnimator } from './TravelAnimator';
import { TravelCatchUp } from './TravelCatchUp';
import { buildPathfindingRequest } from './TravelRequestBuilder';
import { buildTravelRoute } from './TravelRouteBuilder';
import { TravelMode, TravelPhase } from './TravelTypes';

export class TravelSystem implements GameModule {
  readonly name = 'TravelSystem';
  readonly state: TravelState;

  private _events!: EventBus;
  private _animator: TravelAnimator;
  private _catchUp: TravelCatchUp;
  private _config: TravelConfig;
  private _worldInfo: WorldInfo;
  private _fogState: FogState | null;
  private _line: TravelLineRenderer;
  private _fleetRenderer: FleetRenderer;
  private _sunKeys: (() => ReadonlySet<number>) | null;
  private _planetKeys: (() => ReadonlySet<number>) | null;
  private _fleet: FleetState;
  private _indicator: FleetIndicator;
  private _worker: Worker | null = null;
  private _timeoutId = 0;
  private readonly _hopEvt = { q: 0, r: 0 };

  constructor(
    config: TravelConfig, worldInfo: WorldInfo, fogState: FogState | null,
    line: TravelLineRenderer, fleetRenderer: FleetRenderer,
    sunKeys: (() => ReadonlySet<number>) | null,
    planetKeys: (() => ReadonlySet<number>) | null,
    fleet: FleetState, indicator: FleetIndicator,
  ) {
    this._config = config;
    this._worldInfo = worldInfo;
    this._fogState = fogState;
    this._line = line;
    this._fleetRenderer = fleetRenderer;
    this._sunKeys = sunKeys;
    this._planetKeys = planetKeys;
    this._fleet = fleet;
    this._indicator = indicator;
    this.state = new TravelState();
    this._animator = new TravelAnimator(worldInfo.config.hexRadius);
    this._catchUp = new TravelCatchUp(
      this.state, this._animator, config, fogState,
      line, fleetRenderer, fleet, indicator,
    );
  }

  init(engine: Engine): void {
    this._events = engine.events;
    this._worker = new Worker(
      new URL('../workers/pathfinding.worker.ts', import.meta.url),
      { type: 'module' },
    );
    this._worker.onmessage = (e: MessageEvent<PathfindingResponse>) => {
      this._onWorkerResponse(e.data);
    };
    this._events.on<null>('travel:select-start', () => {
      if (this.state.beginSelectStart()) this._emitPhase();
    });
    this._events.on<null>('travel:select-end', () => {
      if (this.state.beginSelectEnd()) this._emitPhase();
    });
    this._events.on<{ q: number; r: number }>('travel:hex-clicked', (d) => this._onHexClicked(d.q, d.r));
    this._events.on<{ mode: TravelMode }>('travel:go', (d) => this._startPathfinding(d.mode));
    this._events.on<null>('travel:cancel', () => this._cancel());
    this._events.on<null>('fleet:enter-flightview', () => { this._line.setSuppressed(true); });
    this._events.on<null>('fleet:exit-flightview', () => { this._line.setSuppressed(false); });
    this._catchUp.init(engine.events);
  }

  update(dt: number): void {
    this._line.advanceTime(dt);
    if (this.state.phase !== TravelPhase.Traveling) return;

    const r = this._animator.update(dt);
    this.state.elapsedSeconds += dt;
    const fh = this._config.flightHeight;
    this._fleet.update(r.worldX, fh, r.worldZ, r.dirX, r.dirZ, r.turnRate);
    this._fleetRenderer.updateFormation();

    if (r.hopCompleted) {
      this.state.currentHopIdx++;
      this._revealAtHex(r.hexQ, r.hexR);
      this._hopEvt.q = r.hexQ; this._hopEvt.r = r.hexR;
      this._events.emit('travel:hop', this._hopEvt);
    }
    if (r.finished) {
      this.state.arrive(); this._emitPhase();
      this._events.emit('travel:arrived', null);
      this._animator.stop(); this._setFleetActive(false);
    }
  }

  dispose(): void {
    if (this._timeoutId) clearTimeout(this._timeoutId);
    this._worker?.terminate();
    this._worker = null;
    this._line.dispose();
  }

  private _onHexClicked(q: number, r: number): void {
    if (this.state.phase === TravelPhase.SelectingStart) {
      this.state.setStart(q, r);
      this._events.emit('travel:start-set', { q, r });
      this.state.beginSelectEnd();
    } else if (this.state.phase === TravelPhase.SelectingEnd) {
      this.state.setEnd(q, r);
      this._events.emit('travel:end-set', { q, r });
    } else { return; }
    this._emitPhase();
  }

  private _startPathfinding(mode: TravelMode): void {
    if (!this.state.beginPathfinding(mode)) return;
    this._emitPhase();
    const sunKeys: number[] = [];
    if (this._sunKeys) {
      for (const k of this._sunKeys()) sunKeys.push(k);
    }
    const msg = buildPathfindingRequest(
      this.state.startHex, this.state.endHex, mode,
      this._config, this._worldInfo, sunKeys,
    );
    this._worker?.postMessage(msg);
    this._timeoutId = window.setTimeout(() => {
      if (this.state.phase === TravelPhase.Pathfinding) this._cancel();
    }, this._config.pathfinding.timeoutMs);
  }

  private _onWorkerResponse(resp: PathfindingResponse): void {
    if (this._timeoutId) { clearTimeout(this._timeoutId); this._timeoutId = 0; }
    if (this.state.phase !== TravelPhase.Pathfinding) return;
    if (resp.type === 'path-error') {
      this._events.emit('travel:error', { message: resp.message });
      this.state.cancel();
      this._emitPhase();
      return;
    }
    const route = buildTravelRoute(resp.route, this.state.mode, this._config);
    this.state.setRoute(route);
    this._emitPhase();
    const rc = this._config.routeColors;
    const nc = this.state.mode === TravelMode.Reveal ? rc.reveal : rc.normal;
    this._line.buildFromHexes(
      resp.route, this._worldInfo.config.hexRadius,
      this._config.flightHeight, route.segmentKinds, nc, rc.warp,
    );
    this._animator.start(route, this._planetKeys?.() ?? null);
    this._setFleetActive(true);
    this._revealAtHex(resp.route[0].q, resp.route[0].r);
  }

  private _revealAtHex(q: number, r: number): void {
    if (!this._fogState) return;
    const c = this._config;
    const m = this.state.mode;
    const rad = m === TravelMode.Reveal ? c.reveal.revealRadius : m === TravelMode.Warp ? c.warp.revealRadius : c.normal.revealRadius;
    if (rad < 0) return;
    this._fogState.addReveal(q, r, rad);
    this._events.emit('fog:reveals-changed', null);
  }

  private _setFleetActive(active: boolean): void {
    this._fleetRenderer.setVisible(active);
    this._fleet.active = active;
    this._indicator.setVisible(active);
    if (!active) this._events.emit('fleet:exit-flightview', null);
  }

  private _cancel(): void {
    this.state.cancel();
    this._animator.stop();
    this._line.clear();
    this._setFleetActive(false);
    this._emitPhase();
  }

  private _emitPhase(): void {
    this._events.emit('travel:phase-changed', { phase: this.state.phase });
    this.state.ackPhaseChange();
  }
}
